import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
	Building2,
	Search,
	Filter,
	Users,
	ChevronLeft,
	ChevronRight,
	ExternalLink,
	Package,
	Crown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import * as api from "@/services/api";

const planColors: Record<string, string> = {
	free: "bg-gray-100 text-gray-700",
	starter: "bg-blue-100 text-blue-700",
	professional: "bg-purple-100 text-purple-700",
	enterprise: "bg-amber-100 text-amber-700",
};

const statusColors: Record<string, string> = {
	active: "bg-green-100 text-green-700",
	inactive: "bg-gray-100 text-gray-700",
	cancelled: "bg-red-100 text-red-700",
	trialing: "bg-blue-100 text-blue-700",
	past_due: "bg-yellow-100 text-yellow-700",
	incomplete: "bg-orange-100 text-orange-700",
};

const plans = ["free", "starter", "professional", "enterprise"];
const statuses = [
	"active",
	"inactive",
	"cancelled",
	"trialing",
	"past_due",
	"incomplete",
];

export function OrganizationList() {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [selectedPlan, setSelectedPlan] = useState<string>("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [showFilters, setShowFilters] = useState(false);

	const { data, isLoading, error } = useQuery({
		queryKey: [
			"admin-organizations",
			page,
			search,
			selectedPlan,
			selectedStatus,
		],
		queryFn: () =>
			api.getAdminOrganizations({
				page,
				limit: 20,
				search: search || undefined,
				plan: selectedPlan || undefined,
				subscriptionStatus: selectedStatus || undefined,
			}),
	});

	const { data: overviewStats } = useQuery({
		queryKey: ["admin-organizations-overview"],
		queryFn: api.getOrganizationsOverview,
	});

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setSearch(searchInput);
		setPage(1);
	};

	const clearFilters = () => {
		setSearch("");
		setSearchInput("");
		setSelectedPlan("");
		setSelectedStatus("");
		setPage(1);
	};

	const hasFilters = search || selectedPlan || selectedStatus;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
					<p className="text-gray-500 mt-1">
						Manage all organizations in the system
					</p>
				</div>
				<Link to="/organizations/new">
					<Button className="bg-blue-600 hover:bg-blue-700">
						<Building2 className="w-4 h-4 mr-2" />
						New Organization
					</Button>
				</Link>
			</div>

			{/* Overview Stats */}
			{overviewStats && (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-100 rounded-lg">
									<Building2 className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overviewStats.organizations.total}
									</p>
									<p className="text-sm text-gray-500">Total Organizations</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-green-100 rounded-lg">
									<Crown className="w-5 h-5 text-green-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overviewStats.organizations.byStatus?.active || 0}
									</p>
									<p className="text-sm text-gray-500">Active Subscriptions</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-purple-100 rounded-lg">
									<Users className="w-5 h-5 text-purple-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overviewStats.users.total}
									</p>
									<p className="text-sm text-gray-500">Total Users</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-amber-100 rounded-lg">
									<Package className="w-5 h-5 text-amber-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{overviewStats.organizations.withCustomPackage}
									</p>
									<p className="text-sm text-gray-500">Custom Packages</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Search and Filters */}
			<Card>
				<CardContent className="p-4">
					<div className="flex flex-col md:flex-row gap-4">
						<form onSubmit={handleSearch} className="flex-1 flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									placeholder="Search organizations..."
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									className="pl-10"
								/>
							</div>
							<Button type="submit" variant="outline">
								Search
							</Button>
						</form>
						<Button
							variant="outline"
							onClick={() => setShowFilters(!showFilters)}
							className={cn(showFilters && "bg-gray-100")}
						>
							<Filter className="w-4 h-4 mr-2" />
							Filters
							{hasFilters && (
								<span className="ml-2 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
									{[search, selectedPlan, selectedStatus].filter(Boolean).length}
								</span>
							)}
						</Button>
					</div>

					{/* Filter Panel */}
					{showFilters && (
						<div className="mt-4 pt-4 border-t flex flex-wrap gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Plan
								</label>
								<select
									value={selectedPlan}
									onChange={(e) => {
										setSelectedPlan(e.target.value);
										setPage(1);
									}}
									className="px-3 py-2 border rounded-md text-sm"
								>
									<option value="">All Plans</option>
									{plans.map((plan) => (
										<option key={plan} value={plan}>
											{plan.charAt(0).toUpperCase() + plan.slice(1)}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Status
								</label>
								<select
									value={selectedStatus}
									onChange={(e) => {
										setSelectedStatus(e.target.value);
										setPage(1);
									}}
									className="px-3 py-2 border rounded-md text-sm"
								>
									<option value="">All Statuses</option>
									{statuses.map((status) => (
										<option key={status} value={status}>
											{status
												.split("_")
												.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
												.join(" ")}
										</option>
									))}
								</select>
							</div>
							{hasFilters && (
								<div className="flex items-end">
									<Button variant="ghost" size="sm" onClick={clearFilters}>
										Clear Filters
									</Button>
								</div>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Organizations Table */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>Organizations</span>
						{data && (
							<span className="text-sm font-normal text-gray-500">
								{data.pagination.total} total
							</span>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
						</div>
					) : error ? (
						<div className="text-center py-12 text-red-500">
							Failed to load organizations
						</div>
					) : data?.organizations.length === 0 ? (
						<div className="text-center py-12 text-gray-500">
							No organizations found
						</div>
					) : (
						<>
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b">
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Organization
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Plan
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Status
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Users
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Created
											</th>
											<th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">
												Actions
											</th>
										</tr>
									</thead>
									<tbody>
										{data?.organizations.map((org) => (
											<tr
												key={org._id}
												className="border-b last:border-0 hover:bg-gray-50"
											>
												<td className="py-3 px-4">
													<div className="flex items-center gap-3">
														<div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
															<Building2 className="w-5 h-5 text-gray-500" />
														</div>
														<div>
															<p className="font-medium text-gray-900">
																{org.name}
															</p>
															<p className="text-sm text-gray-500">{org.slug}</p>
														</div>
														{org.hasCustomPackage && (
															<Badge className="bg-amber-100 text-amber-700">
																Custom
															</Badge>
														)}
													</div>
												</td>
												<td className="py-3 px-4">
													<Badge className={planColors[org.plan] || ""}>
														{org.plan}
													</Badge>
												</td>
												<td className="py-3 px-4">
													<Badge
														className={
															statusColors[org.subscriptionStatus || "inactive"] ||
															""
														}
													>
														{(org.subscriptionStatus || "inactive")
															.split("_")
															.map(
																(s) => s.charAt(0).toUpperCase() + s.slice(1),
															)
															.join(" ")}
													</Badge>
												</td>
												<td className="py-3 px-4">
													{(() => {
														const used = org.seatCount ?? org.userCount ?? 0;
														const purchased = org.purchasedSeats ?? 0;
														const percent = purchased > 0 ? (used / purchased) * 100 : 0;
														return (
															<div className="min-w-[80px]">
																<div className="flex items-center gap-1 text-gray-600">
																	<Users className="w-4 h-4" />
																	<span>
																		{used}{purchased > 0 ? ` / ${purchased}` : ""}
																	</span>
																</div>
																{purchased > 0 && (
																	<div className="w-full h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
																		<div
																			className={cn(
																				"h-full rounded-full transition-all",
																				percent >= 90 ? "bg-red-500" : "bg-blue-500",
																			)}
																			style={{ width: `${Math.min(percent, 100)}%` }}
																		/>
																	</div>
																)}
															</div>
														);
													})()}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{org.createdAt
														? new Date(org.createdAt).toLocaleDateString()
														: "-"}
												</td>
												<td className="py-3 px-4 text-right">
													<Link to={`/organizations/${org._id}`}>
														<Button variant="ghost" size="sm">
															<ExternalLink className="w-4 h-4 mr-1" />
															View
														</Button>
													</Link>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							{data && data.pagination.pages > 1 && (
								<div className="flex items-center justify-between mt-4 pt-4 border-t">
									<p className="text-sm text-gray-500">
										Page {data.pagination.page} of {data.pagination.pages}
									</p>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={page <= 1}
											onClick={() => setPage((p) => p - 1)}
										>
											<ChevronLeft className="w-4 h-4 mr-1" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={page >= data.pagination.pages}
											onClick={() => setPage((p) => p + 1)}
										>
											Next
											<ChevronRight className="w-4 h-4 ml-1" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
